import React from "react";
import "./EventCard.css";

/**
 * EventCard component displays a social event card with expandable details
 *
 * @param {Object} props
 * @param {Object} props.event - The event data to display
 * @param {boolean} props.isExpanded - Whether the card is expanded to show description
 * @param {boolean} props.isSelected - Whether the event is selected
 * @param {Function} props.onToggleExpand - Function to call when toggling expansion
 * @param {Function} props.onSelect - Function to call when selecting the event
 * @returns {JSX.Element}
 */
const EventCard = ({
  event,
  isExpanded,
  isSelected,
  onToggleExpand,
  onSelect,
}) => {
  // Determine if event is already posted to calendar or private
  const isPosted = event.poll && event.poll.length > 5;
  const isPrivate = !event.generalpublic;

  // Random number of attendees (for display purposes)
  const attendeeCount = Math.floor(Math.random() * 7) + 8;

  /**
   * Handle select button click without triggering card expansion
   */
  const handleSelectClick = (e) => {
    e.stopPropagation(); // Prevent card expansion
    onSelect(event.socialEventId);
  };

  return (
    <div
      className={`event-card ${isPosted ? "posted" : ""} ${
        isPrivate ? "private" : ""
      } ${isExpanded ? "expanded" : ""}`}
      onClick={onToggleExpand}
    >
      <div className="event-header">
        <div className="event-title">
          <h3>{event.title}</h3>
          <div className="event-meta">
            <span className="event-short-date">{event.shortDate}</span>
            <span className="event-time">{event.time}</span>
            {event.length && (
              <span className="event-duration">({event.length})</span>
            )}
            <span className="event-attendees">👥 {attendeeCount}</span>
          </div>
        </div>
        <div className="event-actions">
          {isPosted ? (
            <div className="event-badge posted-badge">Posted to Calendar</div>
          ) : isPrivate ? (
            <div className="event-badge private-badge">Private Event</div>
          ) : (
            <button
              onClick={handleSelectClick}
              className={isSelected ? "selected" : ""}
              disabled={isPosted}
            >
              {isSelected ? "Selected ✓" : "Select"}
            </button>
          )}
        </div>
      </div>

      {event.imgKey && (
        <div className="event-image">
          <img
            src={`https://s3.amazonaws.com/social-images-public/${event.imgKey}`}
            alt={event.title}
            loading="lazy"
          />
        </div>
      )}

      <div className="event-details">
        <div className="event-location">
          <span>📍 </span>
          {event.privateLocation ? event.privateLocation : event.location}
        </div>

        {isExpanded && event.description && (
          <div className="event-description">{event.description}</div>
        )}
      </div>
    </div>
  );
};

export default EventCard;
